<h1>Bulk Domain Operations</h1>
<p>Various operations that act on a list of domains uploaded in a text file (.txt) or Excel spreadsheet (.xlsx, .xls). For Excel files, domains should be in the first column of the first sheet.</p>

<div class="op_row shorter">
    <mat-form-field>
        <mat-label>Select flag</mat-label>
        <mat-select [(ngModel)]="selectedFlag"
                    id="flag_select">
            @for (f of flags(); track $index) {
                <mat-option [value]="f">{{f}}</mat-option>
            }
        </mat-select>
    </mat-form-field>

    <input type="file" class="hidden"
           accept=".txt,.xlsx,.xls"
           (change)="setFlagLoaded($event)"
           #setFlagUpload>
    <button mat-raised-button color="primary"
            [disabled]="!selectedFlag()"
            (click)="setFlagUpload.click()">Set flag for Domains...</button>

    <input type="file" class="hidden"
           accept=".txt,.xlsx,.xls"
           (change)="clearFlagLoaded($event)"
           #clearFlagUpload>
    <button mat-raised-button color="primary"
            [disabled]="!selectedFlag()"
            (click)="clearFlagUpload.click()">Clear flag for Domains...</button>
</div>

<div class="op_row">
    <input type="file" class="hidden"
           accept=".txt,.xlsx,.xls"
           (change)="lockLoaded($event)"
           #lockUpload>
    <button mat-raised-button
            color="accent"
            (click)="lockUpload.click()">Lock Domains...</button>

    <input type="file" class="hidden"
           accept=".txt,.xlsx,.xls"
           (change)="unlockLoaded($event)"
           #unlockUpload>
    <button mat-raised-button
            color="accent"
            (click)="unlockUpload.click()">Unlock Domains...</button>
</div>

<div class="op_row shorter">
    <mat-form-field>
        <mat-label>Select target group</mat-label>
        <mat-select [(ngModel)]="selectedGroup"
                    id="group_select">
            @for (g of nsGroups(); track g.name) {
                <mat-option [value]="g.name">
                    {{g.name}}
                </mat-option>
            }
        </mat-select>
    </mat-form-field>

    <input type="file" class="hidden"
           accept=".txt,.xlsx,.xls"
           (change)="dnsLoaded($event)"
           #dnsUpload>
    <button mat-raised-button
            color="accent"
            [disabled]="!selectedGroup()"
            (click)="dnsUpload.click()">Change NS Group...</button>
</div>

<div class="op_row shorter">
    <mat-form-field id="code_field">
        <mat-label>2FA code to authorize</mat-label>
        <input matInput
               placeholder="6-digit code"
               [(ngModel)]="allowDeleteCode">
    </mat-form-field>

    <button mat-raised-button
            color="accent"
            type="button"
            [disabled]="!canAllowDelete()"
            (click)="authorizeDelete()">Authorize Bulk Delete</button>

    <input type="file" class="hidden"
           accept=".txt,.xlsx,.xls"
           (change)="deleteLoaded($event)"
           #deleteUpload>
    <button mat-raised-button
            color="warn"
            type="button"
            [disabled]="!canDelete()"
            (click)="deleteClicked()">Delete Domains...</button>
</div>

<div [hidden]="taskGroups().length==0" class="paginator_row">
    <span id="table_title">Bulk Domain Task Groups</span>
    <mat-paginator #paginator
                   [length]="taskGroups().length"
                   [pageIndex]="0"
                   [pageSize]="DEFAULT_PAGE_SIZE"
                   [pageSizeOptions]="[5, 10, 20]"></mat-paginator>
</div>
<div id="table_scroller">
    <table mat-table [dataSource]="dataSource()">

        <ng-container matColumnDef="id">
            <th mat-header-cell *matHeaderCellDef>ID</th>
            <td mat-cell *matCellDef="let group" [innerHTML]="group.id"
                class="link"
                matTooltip="View all group tasks"
                matTooltipPosition="before"
                (click)="goToGroup(group.id)"></td>
        </ng-container>

        <ng-container matColumnDef="type">
            <th mat-header-cell *matHeaderCellDef>Type</th>
            <td mat-cell *matCellDef="let group" [innerHTML]="group.type"></td>
        </ng-container>

        <ng-container matColumnDef="other">
            <th mat-header-cell *matHeaderCellDef>Other</th>
            <td mat-cell *matCellDef="let group" [innerHTML]="group.other"></td>
        </ng-container>

        <ng-container matColumnDef="origin">
            <th mat-header-cell *matHeaderCellDef>Origin</th>
            <!-- Which backend enqueued this group. Both deployments share one
                 database and each dashboard lists both partitions on purpose,
                 so staging-initiated groups have to stand out. -->
            <td mat-cell *matCellDef="let group">
                <span class="origin"
                      [class.staging]="group.origin === 'staging'">{{group.origin}}</span>
            </td>
        </ng-container>

        <ng-container matColumnDef="user">
            <th mat-header-cell *matHeaderCellDef>User</th>
            <!-- Who enqueued the group; empty for groups older than this column. -->
            <td mat-cell *matCellDef="let group"
                [matTooltip]="group.user_email || ''"
                matTooltipPosition="before">{{group.user_email || ''}}</td>
        </ng-container>

        <ng-container matColumnDef="created">
            <th mat-header-cell *matHeaderCellDef>Created</th>
            <td mat-cell *matCellDef="let group" [innerHTML]="formatTimestamp(group.created)"></td>
        </ng-container>

        <ng-container matColumnDef="processed">
            <th mat-header-cell *matHeaderCellDef>Processed</th>
            <td mat-cell *matCellDef="let group" [innerHTML]="formatTimestamp(group.processed)"></td>
        </ng-container>

        <ng-container matColumnDef="time_left">
            <th mat-header-cell *matHeaderCellDef>Time Left [hr]</th>
            <td mat-cell *matCellDef="let group" [innerHTML]="estimateTimeLeft(group)"></td>
        </ng-container>`

        <ng-container matColumnDef="task_count">
            <th mat-header-cell *matHeaderCellDef>Count</th>
            <td mat-cell *matCellDef="let group" [innerHTML]="group.task_count | number"></td>
        </ng-container>

        <ng-container matColumnDef="completed_task_count">
            <th mat-header-cell *matHeaderCellDef>Completed</th>
            <td mat-cell *matCellDef="let group" [innerHTML]="group.completed_task_count | number"></td>
        </ng-container>

        <ng-container matColumnDef="failed_task_count">
            <th mat-header-cell *matHeaderCellDef>Failed</th>
            <td mat-cell *matCellDef="let group" [innerHTML]="group.failed_task_count | number"></td>
        </ng-container>

        <tr mat-header-row *matHeaderRowDef="columnsToDisplay"></tr>
        <tr mat-row *matRowDef="let _; columns: columnsToDisplay"></tr>

    </table>
</div>
